import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

const STAR_COUNT = 400;

export default function StarfieldTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.stars = Array.from({ length: STAR_COUNT }, () => ({
        x: (Math.random() - 0.5) * w,
        y: (Math.random() - 0.5) * h,
        z: Math.random() * w,
        colorIdx: Math.floor(Math.random() * palette.length),
        twinklePhase: Math.random() * Math.PI * 2,
      }));
      state.shooters = [];
      state.nextShoot = null;
    },
    draw: (ctx, w, h, state, t) => {
      ctx.fillStyle = dark ? 'rgba(3,7,18,0.35)' : 'rgba(249,250,251,0.35)';
      ctx.fillRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const speed = 6;

      for (const s of state.stars) {
        s.z -= speed;
        if (s.z <= 1) {
          s.x = (Math.random() - 0.5) * w;
          s.y = (Math.random() - 0.5) * h;
          s.z = w;
          s.colorIdx = Math.floor(Math.random() * palette.length);
        }
        const k = 128 / s.z;
        const px = s.x * k + cx;
        const py = s.y * k + cy;
        if (px < 0 || px >= w || py < 0 || py >= h) continue;

        const nearness = 1 - s.z / w;
        const size = nearness * 3;
        const colorHex = palette[(s.colorIdx + Math.floor(t * (colorMode === 'cycling' ? 0.8 : 0))) % palette.length];
        const { r, g, b } = hexToRgb(colorHex);
        const twinkle = 0.75 + 0.25 * Math.sin(t * 3 + s.twinklePhase);
        const alpha = nearness * (dark ? 1 : 0.7) * twinkle;

        // Nearer stars get a soft bloom (dark mode only).
        ctx.shadowBlur = dark ? nearness * 8 : 0;
        ctx.shadowColor = `rgba(${r},${g},${b},0.9)`;
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.beginPath();
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      // Occasional shooting star streaking across the field
      if (state.nextShoot == null) state.nextShoot = t + 4 + Math.random() * 8;
      if (t >= state.nextShoot) {
        const fromLeft = Math.random() < 0.5;
        state.shooters.push({
          x: fromLeft ? Math.random() * w * 0.3 : w - Math.random() * w * 0.3,
          y: Math.random() * h * 0.45,
          vx: (fromLeft ? 1 : -1) * (7 + Math.random() * 5),
          vy: 2.5 + Math.random() * 2.5,
          life: 1,
        });
        state.nextShoot = t + 6 + Math.random() * 10;
      }
      state.shooters = state.shooters.filter(sh => {
        sh.x += sh.vx;
        sh.y += sh.vy;
        sh.life -= 0.02;
        if (sh.life <= 0 || sh.x < -60 || sh.x > w + 60 || sh.y > h + 60) return false;
        const tailX = sh.x - sh.vx * 6;
        const tailY = sh.y - sh.vy * 6;
        const grad = ctx.createLinearGradient(tailX, tailY, sh.x, sh.y);
        grad.addColorStop(0, 'rgba(226,232,240,0)');
        grad.addColorStop(1, `rgba(226,232,240,${(dark ? 0.9 : 0.6) * sh.life})`);
        ctx.save();
        ctx.shadowBlur = dark ? 10 : 4;
        ctx.shadowColor = `rgba(226,232,240,${0.9 * sh.life})`;
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(sh.x, sh.y);
        ctx.stroke();
        // Bright head
        ctx.fillStyle = `rgba(255,255,255,${0.9 * sh.life})`;
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        return true;
      });
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
